/**
 * The only ownershipInfo fields the public site renders
 * (components/listing/details/specs-additional-details/SpecsAdditionalDetailsModel.ts).
 *
 * Next.js serialises every prop into the page's __NEXT_DATA__ script, so anything left on a
 * listing object is published in the page source whether or not it is displayed. The seller's
 * loan account number, bank, loan amount, lender phone and co-seller details, and the title
 * photo's storage key, reached public, crawlable listing and marketplace pages that way (PA-6195). The prelogin API no longer returns
 * them; this keeps the site from republishing them if an upstream response ever does again.
 */
const PUBLIC_OWNERSHIP_FIELDS = [
  "vehicleHaveLien",
  "carTitle",
  "ownershipType",
  "titlePossession",
] as const;

export function toPublicOwnershipInfo(
  ownershipInfo: unknown
): Record<string, unknown> | null {
  // null, not undefined: Next.js rejects undefined anywhere in page props.
  if (!ownershipInfo || typeof ownershipInfo !== "object") return null;
  const source = ownershipInfo as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const field of PUBLIC_OWNERSHIP_FIELDS) {
    if (source[field] !== undefined) out[field] = source[field];
  }
  return out;
}

// Never rendered, and private: the title photo's storage key was enough to download it, and
// the payment record is the seller's (PA-6195).
const PRIVATE_LISTING_FIELDS = ["titleAttachment", "payment"] as const;

function narrowListing(listing: unknown): void {
  if (!listing || typeof listing !== "object") return;
  const l = listing as Record<string, unknown>;
  if ("ownershipInfo" in l) l.ownershipInfo = toPublicOwnershipInfo(l.ownershipInfo);
  for (const field of PRIVATE_LISTING_FIELDS) delete l[field];
}

/**
 * Narrows every listing in a prelogin listings response body (`data` and
 * `similarListings`), in place.
 */
export function withPublicListings<T>(body: T): T {
  if (!body || typeof body !== "object") return body;
  const b = body as Record<string, unknown>;
  for (const key of ["data", "similarListings"]) {
    const value = b[key];
    if (Array.isArray(value)) value.forEach(narrowListing);
    else narrowListing(value);
  }
  return body;
}
